// « Balance » — the trial balance of a month, account by account (docs/PMS-LEDGER-UI-BRIEF.md §2, panel 2).
//
// Read-only. The reporting period is the calendar month, so the balance is read month by month, the
// current month first. The figures come from `fin_trial_balance` (the EFFECTIVE ledger); the sums that
// must agree are checked by fin-core and SHOWN: a balance whose debits differ from its credits is a
// defect and says so in red, it is never smoothed over.
//
// Honesty: a missing right is « Droit manquant », never an empty table; a month with no entry says so
// (that is an answer, not an error); a failed read says the balance is unreadable and can be retried.

import { useCallback, useEffect, useState } from 'react';
import {
  balanceSide,
  currentMonthParis,
  formatAmount,
  formatMonthLong,
  isMonth,
  monthRange,
  shiftMonth,
  trialBalanceTotals,
  type LedgerReader,
  type TrialBalanceLine,
} from '@lemontaigu/fin-core';
import { LignesCompte } from './LignesCompte.js';

// ── the pure view ───────────────────────────────────────────────────────────

/** An account's balance as two cells: the amount (right-aligned with every other amount) and the side it
 *  stands on, in a narrow column of its own so the amounts line up on the decimal comma. */
function Solde({ netCents, header = false }: { netCents: number; header?: boolean }) {
  const s = balanceSide(netCents);
  const Cell = header ? 'th' : 'td';
  return (
    <>
      <Cell className="fin-num">{s.side === null ? '—' : formatAmount(s.amountCents)}</Cell>
      <Cell className="fin-side">{s.side ?? ''}</Cell>
    </>
  );
}

export function BalanceView({
  lines,
  month,
  onAccount,
}: {
  lines: readonly TrialBalanceLine[];
  month: string;
  /** Opens the lines of an account (the drill-down). Absent: the account code is plain text. */
  onAccount?: (line: TrialBalanceLine) => void;
}) {
  if (lines.length === 0) {
    return (
      <p className="muted" data-testid="fin-balance-empty">
        Aucune écriture pour {formatMonthLong(month)}.
      </p>
    );
  }
  const totals = trialBalanceTotals(lines);
  const gap = totals.debitCents - totals.creditCents;
  return (
    <div data-testid="fin-balance">
      <div className="pms-table-wrap">
        <table className="pms-table fin-balance-table">
          <thead>
            <tr>
              <th>Compte</th>
              <th>Libellé</th>
              <th className="fin-num">Débit</th>
              <th className="fin-num">Crédit</th>
              <th className="fin-num" colSpan={2}>Solde</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.account} data-account={l.account}>
                <td>
                  {onAccount ? (
                    <button type="button" className="fin-link" onClick={() => onAccount(l)}>{l.account}</button>
                  ) : (
                    l.account
                  )}
                </td>
                <td>{l.label}</td>
                <td className="fin-num">{l.debitCents === 0 ? '' : formatAmount(l.debitCents)}</td>
                <td className="fin-num">{l.creditCents === 0 ? '' : formatAmount(l.creditCents)}</td>
                <Solde netCents={l.netCents} />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr data-testid="fin-balance-total">
              <th colSpan={2}>Total</th>
              <th className="fin-num">{formatAmount(totals.debitCents)}</th>
              <th className="fin-num">{formatAmount(totals.creditCents)}</th>
              <Solde netCents={gap} header />
            </tr>
          </tfoot>
        </table>
      </div>
      {totals.balanced && totals.linesConsistent ? (
        <p className="fin-agree" data-testid="fin-balance-ok">Balance équilibrée : le total des débits égale celui des crédits.</p>
      ) : (
        <p className="fin-health fin-health--alert" role="alert" data-testid="fin-balance-defect">
          {!totals.balanced
            ? `Balance déséquilibrée : écart de ${formatAmount(Math.abs(gap))} entre débits et crédits. C'est un défaut du livre, pas une présentation.`
            : 'Lecture incohérente : le solde d’un compte n’est pas son débit moins son crédit.'}
        </p>
      )}
    </div>
  );
}

// ── the container ───────────────────────────────────────────────────────────

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; lines: readonly TrialBalanceLine[] }
  | { phase: 'forbidden' }
  | { phase: 'error'; message: string };

export function Balance({ ledger, book, now }: { ledger: LedgerReader; book: string; now?: Date }) {
  const [month, setMonth] = useState(() => currentMonthParis(now ?? new Date()));
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [account, setAccount] = useState<TrialBalanceLine | null>(null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    setAccount(null);
    const { from, to } = monthRange(month);
    const r = await ledger.trialBalance(book, from, to);
    if (r.ok) setState({ phase: 'ready', lines: r.value });
    else if (r.error.kind === 'forbidden') setState({ phase: 'forbidden' });
    else setState({ phase: 'error', message: r.error.message });
  }, [ledger, book, month]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="fin-balance-panel" aria-label="Balance">
      <h3>Balance</h3>
      <div className="fin-month">
        <button type="button" aria-label="Mois précédent" onClick={() => setMonth(shiftMonth(month, -1))}>‹</button>
        <input
          type="month"
          aria-label="Mois"
          value={month}
          onChange={(e) => isMonth(e.target.value) && setMonth(e.target.value)}
        />
        <button type="button" aria-label="Mois suivant" onClick={() => setMonth(shiftMonth(month, 1))}>›</button>
        <strong data-testid="fin-month-label">{formatMonthLong(month)}</strong>
      </div>
      {state.phase === 'loading' && <p className="muted" data-testid="fin-balance-loading">Chargement de la balance…</p>}
      {state.phase === 'forbidden' && (
        <div className="pms-warn" role="alert" data-testid="fin-balance-forbidden">
          Droit manquant : la balance est réservée à l’administrateur.
        </div>
      )}
      {state.phase === 'error' && (
        <div className="pms-warn" role="alert" data-testid="fin-balance-error">
          <p>La balance est illisible : ne rien en conclure. ({state.message})</p>
          <button type="button" onClick={() => void load()}>Réessayer</button>
        </div>
      )}
      {state.phase === 'ready' && <BalanceView lines={state.lines} month={month} onAccount={setAccount} />}
      {state.phase === 'ready' && account && (
        <LignesCompte ledger={ledger} book={book} month={month} account={account} onClose={() => setAccount(null)} />
      )}
    </section>
  );
}
